import {
  DEFAULT_DATE_FORMAT,
  ISO_DATE_FORMAT,
  NO_DATA_PLACEHOLDER,
} from '@sib/config';
import {
  addDays,
  addMinutes,
  addYears,
  format,
  formatDuration,
  intervalToDuration,
  isSameDay,
  isWithinInterval,
  parseISO,
  startOfDay,
  subYears,
} from 'date-fns';

export const CALENDAR_START_MONTH = subYears(new Date(), 5);
export const CALENDAR_END_MONTH = addYears(new Date(), 5);

export const shortenDurationString = (duration: string) =>
  duration.replace(/ hours?/, ' hr').replace(/ minutes?/, ' min');

export const minutesToDuration = (minutes: number) => {
  const start = new Date(0);
  const end = addMinutes(start, minutes);

  const duration = intervalToDuration({ start, end });

  return shortenDurationString(
    formatDuration(duration, { format: ['hours', 'minutes'], delimiter: ' ' })
  );
};

export const isDateValid = (date: Date) => !Number.isNaN(date.getTime());

export const isSameDateRange = (
  a: { from: Date; to: Date },
  b: { from: Date; to: Date }
) => isSameDay(a.from, b.from) && isSameDay(a.to, b.to);

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Parses a date-only (`yyyy-MM-dd`) string as local midnight to avoid UTC shifts. */
export const parseLocalDate = (value?: string | null): Date | null => {
  if (!value) return null;

  const date = ISO_DATE_ONLY.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  return isDateValid(date) ? date : null;
};

export const toInputDate = (value?: string | null) => {
  const date = parseLocalDate(value);
  if (!date) return '';

  return format(date, ISO_DATE_FORMAT);
};

export const toDateOrNull = (value?: string | Date | null): Date | null => {
  if (!value) return null;

  if (value instanceof Date) return isDateValid(value) ? value : null;

  return parseLocalDate(value);
};

/** Parses an ISO date/datetime string (e.g. URL `yyyy-MM-dd`); null if missing or invalid. */
export const parseISODateOrNull = (value?: string | null): Date | null => {
  if (!value) return null;

  const date = parseISO(value);

  return isDateValid(date) ? date : null;
};

export const toISODateOrNull = (value?: string | Date | null) =>
  toDateOrNull(value)?.toISOString() ?? null;

/** Calendar day in local time as `yyyy-MM-dd` — the shape date-only API fields take. */
export const toISODateString = (date: Date) => format(date, 'yyyy-MM-dd');

export const isDateWithinDays = (
  date: string | null | undefined,
  withinDays: number,
  referenceDate: Date = new Date()
): boolean => {
  const target = toDateOrNull(date);
  if (!target) return false;

  const start = startOfDay(referenceDate);
  const end = addDays(start, withinDays);

  return isWithinInterval(startOfDay(target), { start, end });
};

interface FormatDateProps {
  value?: string | null;
  dateFormat?: string;
}

export const formatCellDate = ({
  value,
  dateFormat = DEFAULT_DATE_FORMAT,
}: FormatDateProps) => {
  if (!value) return NO_DATA_PLACEHOLDER;

  const date = new Date(value);

  return isDateValid(date) ? format(date, dateFormat) : NO_DATA_PLACEHOLDER;
};
